import { FileSystemErrorCodes } from "app/modules/remote-control-ng/definitions";
import { FileSystemContext } from "../../remote-control-ng/context/fileSystem";
import { RcContext } from "../../remote-control-ng/context/rcContext";
import { DosCompressItemMessage, DosDeleteFileMessage, DosDeleteFolderMessage, DosEmptyFolderMessage, DosGetDirMessage, DosGetFileDataMessage, DosGetFileMessage, DosMakeDirectoryMessage, DosQueryDeviceMessage, DosRenameFileMessage, DosSearchMessage, DosSendFileDataMessage, DosSendFileMessage } from "../messages/pocket-com";
import { BaseCommand } from "./baseCommand";
import { RequestReplyService } from "../../remote-control-ng/services/requestReplyService"
import { InjectionContextCommand } from "../commandFactory";
import { CallbackType, DataCallbackType } from "../mcrc-api";
import { Injector } from "@angular/core";

@InjectionContextCommand
export class QueryDeviceCommand extends BaseCommand<DosQueryDeviceMessage> {
    constructor(private injector: Injector) {
        super(DosQueryDeviceMessage);
    }

    protected override doProcess(message: DosQueryDeviceMessage): void {
        const requestReplyService = this.injector.get(RequestReplyService);
        if (message.errorCode != 0) {
            requestReplyService.processReply(message);
        } else if (!!message.drives) {
            if (!message.drives.length) {
                // the 2 is used to trigger the fallback when the errorCode is 0, especially for the linux
                requestReplyService.cancel(message.reqId, 2);
            } else {
                requestReplyService.processReply(message);
            }
        } else {
            requestReplyService.cancel(message.reqId);
        }
    }
}

@InjectionContextCommand
export class GetDirCommand extends BaseCommand<DosGetDirMessage> {
    constructor(private injector: Injector) { super(DosGetDirMessage); }

    protected override doProcess(message: DosGetDirMessage): void {
        const fsContext = RcContext.instance.fileSystem;
        const path = fsContext.folderCallbackPath.get(message.reqId);
        for (var item of message.response.tree) {
            item.data.fullPath = path.replace("*", item.data.fileName);
        }
        fsContext.folderCallbackPath.delete(message.reqId);
        this.injector.get(RequestReplyService).processReply(message);
    }
}

@InjectionContextCommand
export class FileSearchCommand extends BaseCommand<DosSearchMessage> {
    constructor(private injector: Injector) {
        super(DosSearchMessage);
    }

    protected override doProcess(message: DosSearchMessage): void {
        const fsContext = RcContext.instance.fileSystem;
        // If the request was cancelled, then there will be no callback to call.
        // If this is the case, then we don't really care about this response
        const requestReplyService = this.injector.get(RequestReplyService);
        requestReplyService.processReply(message, message.response);
        if (message.response.done) {
            requestReplyService.cancel(message.reqId);
        }
    }
}

export class GetFileCommand extends BaseCommand<DosGetFileMessage> {
    constructor() {
        super(DosGetFileMessage);
    }

    protected override doProcess(message: DosGetFileMessage): void {
        const fsContext = RcContext.instance.fileSystem;
        const err = message.errorCode;

        if (err == 0) {
            try {
                fsContext.fileDownloadStop = false;
                fsContext.fileSize = message.fileSize;
                fsContext.fileArray = new Uint8Array(fsContext.fileSize);
                fsContext.fileOffset = 0;
                RcContext.instance.session.sendCallbackRequest(new DosGetFileDataMessage(
                    fsContext.fileOffset,
                    FileSystemContext.blockSize
                ));
            } catch (error) {
                if (fsContext.fileCallback) {
                    const errorCode = error.name === RangeError.prototype.name
                        ? FileSystemErrorCodes.FileMemoryAllocation
                        : FileSystemErrorCodes.FatalError;

                    fsContext.fileCallback(undefined, errorCode);
                }
            }
        } else {
            fsContext.fileCallback(undefined, err);
        }
    }
}

@InjectionContextCommand
export class DeleteFileCommand extends BaseCommand<DosDeleteFileMessage> {
    constructor(private injector: Injector) { super(DosDeleteFileMessage) }

    protected override doProcess(message: DosDeleteFileMessage): void {
        this.injector.get(RequestReplyService).processReply(message);
    }
}

@InjectionContextCommand
export class CompressItemCommand extends BaseCommand<DosCompressItemMessage> {
    constructor(private injector: Injector) {
        super(DosCompressItemMessage)
    }

    protected override doProcess(message: DosCompressItemMessage): void {
        const fileName = message.errorCode !== 0 ? undefined : message.fileName;
        this.injector.get(RequestReplyService).processReply(message);
    }
}

@InjectionContextCommand
export class DeleteFolderCommand extends BaseCommand<DosDeleteFolderMessage> {
    constructor(private injector: Injector) {
        super(DosDeleteFolderMessage)
    }

    protected doProcess(message: DosDeleteFolderMessage): void {
        this.injector.get(RequestReplyService).processReply(message);
    }
}

@InjectionContextCommand
export class EmptyFolderCommand extends BaseCommand<DosEmptyFolderMessage> {
    constructor(private injector: Injector) {
        super(DosEmptyFolderMessage)
    }

    protected doProcess(message: DosEmptyFolderMessage): void {
        this.injector.get(RequestReplyService).processReply(message);
    }
}

export class GetFileDataCommand extends BaseCommand<DosGetFileDataMessage> {
    constructor() { super(DosGetFileDataMessage) }

    protected doProcess(message: DosGetFileDataMessage): void {
        const fsContext = RcContext.instance.fileSystem;
        const err = fsContext.fileDownloadStop ? FileSystemErrorCodes.FileDownloadStopRequested : message.errorCode;

        if (err != 0) {
            fsContext.fileCallback(undefined, err);
        } else {
            if (fsContext.fileArray.length < message.response.offsetArray.length + fsContext.fileOffset) {
                let newFileArray = new Uint8Array(message.response.offsetArray.length + fsContext.fileOffset);
                newFileArray.set(fsContext.fileArray, 0);
                // Remove data from memory.
                fsContext.fileArray = null;
                fsContext.fileArray = newFileArray;
            }
            fsContext.fileArray.set(message.response.offsetArray, fsContext.fileOffset);
            // Remove data from memory.
            message.response.offsetArray = null;
            if (fsContext.fileProgressCallback !== undefined) {
                fsContext.fileProgressCallback(fsContext.fileName, fsContext.fileOffset, fsContext.fileSize);
            }
            fsContext.fileOffset += FileSystemContext.blockSize;
            if (fsContext.fileOffset < fsContext.fileSize) {
                const message = new DosGetFileDataMessage(fsContext.fileOffset, FileSystemContext.blockSize);
                // this is currently working on callbacks. Re-register the same
                RcContext.instance.session.sendCallbackRequest(message);
            } else {
                fsContext.fileCallback(fsContext.fileArray, 0);
            }
        }
    }
}

@InjectionContextCommand
export class RenameFileCommand extends BaseCommand<DosRenameFileMessage> {
    constructor(private injector: Injector) {
        super(DosRenameFileMessage)
    }

    protected doProcess(message: DosRenameFileMessage): void {
        this.injector.get(RequestReplyService).processReply(message);
    }
}

@InjectionContextCommand
export class MakeDirectoryCommand extends BaseCommand<DosMakeDirectoryMessage> {
    constructor(private injector: Injector) {
        super(DosMakeDirectoryMessage);
    }

    protected doProcess(message: DosMakeDirectoryMessage): void {
        this.injector.get(RequestReplyService).processReply(message);
    }
}


@InjectionContextCommand
export class SendFileCommand extends BaseCommand<DosSendFileMessage> {
    constructor(private injector: Injector) {
        super(DosSendFileMessage);
    }

    protected doProcess(message: DosSendFileMessage): void {
        this.injector.get(RequestReplyService).processReply(message);
    }
}

@InjectionContextCommand
export class SendFileDataCommand extends BaseCommand<DosSendFileDataMessage> {
    constructor(private injector: Injector) {
        super(DosSendFileDataMessage);
    }

    protected doProcess(message: DosSendFileDataMessage): void {
        this.injector.get(RequestReplyService).processReply(message);
    }
}
